import React from 'react';
import { useQuery } from '@apollo/client';
import { Link, Stack, Tooltip, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { FormattedMessage } from 'react-intl';
import { GET_ISAD_DETAIL_QUERY, IsadDetail } from '../../../gql';
import { SelectedEntityCloseButton } from '../SelectedEntityCloseButton';

const headingStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  fontWeight: 500,
  color: 'text.primary',
  '&:hover': { color: 'primary.main' },
};
const iconStyles = { fontSize: 16, flexShrink: 0 };
const metadataStyles = (theme: Theme) => ({
  '& > span + span::before': {
    content: '" • "',
    color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
  },
});

const renderMetadataValue = (value: string, index: number) => (
  <span key={index}>{value}</span>
);

export const IsadEntity = React.memo(
  ({ id, name }: { id: number; name: string }) => {
    const { data, loading, error } = useQuery<
      { getIsadDetail: IsadDetail | null },
      { id: number }
    >(GET_ISAD_DETAIL_QUERY, { variables: { id } });
    const detail = data?.getIsadDetail;
    const location = [
      detail?.cadastralArea,
      detail?.municipality ? `(${detail.municipality})` : null,
    ]
      .filter(Boolean)
      .join(' ');
    const metadata = [detail?.type, detail?.era, detail?.impulse]
      .filter(Boolean)
      .map(renderMetadataValue);
    const description = detail?.description || '';
    const shortened = description.length > 132;
    const preview = shortened
      ? `${description.slice(0, 132)}…`
      : description;
    const status = loading ? (
      <FormattedMessage
        id="map.isad.loading"
        defaultMessage="Loading ISAD…"
      />
    ) : (
      <FormattedMessage
        id="map.isad.detailError"
        defaultMessage="Details could not be loaded. Open the ISAD site using the title above."
      />
    );

    return (
      <Stack
        my={1}
        pr={4}
        gap={0.75}
        position="relative"
        data-selected-entity-card
      >
        <SelectedEntityCloseButton />
        <Link
          href={`https://isad.npu.cz/${id}`}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          sx={headingStyles}
        >
          <AccountBalanceOutlinedIcon titleAccess="ISAD" sx={iconStyles} />
          {name}
        </Link>
        {loading || error || !detail ? (
          <Typography variant="body2" color="text.secondary" role="status">
            {status}
          </Typography>
        ) : (
          <>
            <Stack gap={0}>
              {location && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  lineHeight={1.25}
                >
                  {location}
                </Typography>
              )}
              {metadata.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  lineHeight={1.25}
                  sx={metadataStyles}
                >
                  {metadata}
                </Typography>
              )}
            </Stack>
            {description && (
              <Tooltip title={shortened ? description : ''}>
                <Typography
                  variant="body2"
                  fontSize={13}
                  tabIndex={shortened ? 0 : undefined}
                >
                  {preview}
                </Typography>
              </Tooltip>
            )}
          </>
        )}
      </Stack>
    );
  }
);
